import { Layout } from "@/components/layout/Layout";
import { StatsCard } from "@/components/learning/StatsCard";
import { StatsCardSkeleton } from "@/components/learning/StatsCardSkeleton";
import { CourseCardSkeleton } from "@/components/courses/CourseCardSkeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { BookOpen, Award, Clock, TrendingUp, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useUser } from "@/contexts/UserContext";
import { useMyLearningStats } from "@/hooks/MyLearning/useMyLearningStats";
import { useMyLearning } from "@/hooks/MyLearning/useMyLearning";
import { usePaginatedData } from "@/hooks/usePaginatedData";
import { useBrands } from "@/hooks/Organization/useBrand";
import { useDepartments } from "@/hooks/Organization/useDepartment";
import { usePositions } from "@/hooks/Organization/usePosition";
import {
  getBrandName,
  getDepartmentName,
  getPositionName,
} from "@/lib/organization-utils";
import { CourseCard } from "@/components/courses/CourseCard";
import { useCourseImageUrls } from "@/hooks";
import { PaginationControls } from "@/components/ui/pagination-controls";

const Dashboard = () => {
  const { user } = useUser();

    if (!user) {
      return null;
    }

  const {
    stats,
    isLoading: isLoadingStats,
    isError: isErrorStats,
  } = useMyLearningStats();


  // Pagination for in-progress courses
  const inProgressPagination = usePaginatedData({ pageSize: 6 });
  const {
    courses: inProgressCourses,
    total: inProgressTotal,
    isLoading: isLoadingInProgress,
    isError: isErrorInProgress,
  } = useMyLearning(
    inProgressPagination.pagination.pageIndex,
    inProgressPagination.pagination.pageSize,
    "in-progress",
    ""
  );

  const { data: imageUrls } = useCourseImageUrls([...inProgressCourses.map((c) => c.id)], 60);

  const isLoading = isLoadingStats || isLoadingInProgress;
  const isError = isErrorStats || isErrorInProgress;

  const { data: brands = [], isLoading: brandsLoading } = useBrands();
  const { data: departments = [], isLoading: departmentsLoading } =
    useDepartments(Number(user.brandId));
  const { data: positions = [], isLoading: positionsLoading } = usePositions(
    Number(user.brandId),
    Number(user.departmentId)
  );

  const brandName = getBrandName(brands, Number(user.brandId));
  const departmentName = getDepartmentName(
    departments,
    Number(user.departmentId)
  );
  const positionName = getPositionName(positions, Number(user.positionId));

  return (
    <Layout title="Dashboard" description="Tu panel de control personal">
      <div className="w-full max-w-7xl mx-auto px-4 py-4 md:py-6 space-y-6 md:space-y-8">
        <section className="relative overflow-hidden rounded-xl md:rounded-2xl bg-[#363636] border border-[#ef3f47]/20 p-6 md:p-8 lg:p-12 shadow-xl">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[rgb(239,63,71)] rounded-full blur-3xl opacity-10 -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary rounded-full blur-3xl opacity-10 translate-y-1/2 -translate-x-1/2" />

          <div className="relative z-10 space-y-4 md:space-y-5 max-w-3xl">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight animate-fade-in">
              ¡Bienvenido,{" "}
              <span className="text-[rgb(239,63,71)]">{user.userName}</span>!
            </h1>
            <p
              className="text-base md:text-lg lg:text-xl text-white/80 leading-relaxed animate-fade-in"
              style={{ animationDelay: "0.1s" }}
            >
              Continúa tu desarrollo profesional en Grupo Jade. Tienes{" "}
              <span className="font-semibold text-primary">
                {stats.inProgressCoursesCount}{" "}
                {stats.inProgressCoursesCount === 1 ? "curso" : "cursos"}
              </span>{" "}
              en progreso.
            </p>
            <div
              className="flex flex-wrap gap-2 pt-2 animate-fade-in"
              style={{ animationDelay: "0.2s" }}
            >
              <span className="px-4 py-2 rounded-full bg-[rgb(239,63,71)]/10 text-[rgb(239,63,71)] text-sm font-semibold border border-[rgb(239,63,71)]/20 shadow-sm overflow-hidden  text-ellipsis">
                {brandsLoading ? "Cargando..." : brandName || "Sin marca"}
              </span>
              <span className="px-4 py-2 rounded-full bg-[rgb(239,63,71)]/10 text-[rgb(239,63,71)] text-sm font-semibold border border-[rgb(239,63,71)]/20 shadow-sm overflow-hidden  text-ellipsis">
                {departmentsLoading
                  ? "Cargando..."
                  : departmentName || "Sin departamento"}
              </span>
              <span className="px-4 py-2 rounded-full bg-[rgb(239,63,71)]/10 text-[rgb(239,63,71)] text-sm font-semibold border border-[rgb(239,63,71)]/20 shadow-sm overflow-hidden  text-ellipsis">
                {positionsLoading
                  ? "Cargando..."
                  : positionName || "Sin posición"}
              </span>
            </div>
            <div
              className="flex flex-col sm:flex-row gap-3 pt-4 animate-fade-in"
              style={{ animationDelay: "0.3s" }}
            >
              <Link to="/courses" className="flex-1 sm:flex-none">
                <Button
                  size="lg"
                  className="w-full sm:w-auto min-h-[44px] bg-primary text-white hover:bg-primary-hover hover:text-white transition-colors duration-150"
                >
                  Explorar Cursos
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {isErrorStats && !isLoadingStats && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Ocurrió un error al cargar tus datos. Por favor, intenta
              nuevamente.
            </AlertDescription>
          </Alert>
        )}

        <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {isLoading ? (
            <>
              <StatsCardSkeleton />
              <StatsCardSkeleton />
              <StatsCardSkeleton />
              <StatsCardSkeleton />
            </>
          ) : (
            <>
              <Link
                to="/my-learning?tab=completed"
                className="block animate-fade-in"
                style={{ animationDelay: "0.1s" }}
              >
                <StatsCard
                  title="Cursos Completados"
                  value={stats.completedCoursesCount}
                  icon={Award}
                  trend="+3 este mes"
                  clickable
                  variant="redValueOnBlack"
                />
              </Link>
              <div
                className="animate-fade-in"
                style={{ animationDelay: "0.2s" }}
              >
                <StatsCard
                  title="En Progreso"
                  value={stats.inProgressCoursesCount}
                  icon={BookOpen}
                  trend="28.5h este mes"
                />
              </div>
              <div
                className="animate-fade-in"
                style={{ animationDelay: "0.3s" }}
              >
                <StatsCard
                  title="Horas Acumuladas"
                  value={(stats.totalTimeViewedMinutes / 60).toFixed(1)}
                  icon={Clock}
                  trend="28.5h este mes"
                  variant="blackOnWhite"
                />
              </div>
              <div
                className="animate-fade-in"
                style={{ animationDelay: "0.4s" }}
              >
                <StatsCard
                  title="Nivel de Avance"
                  value={`${stats.progressPercentage}%`}
                  icon={TrendingUp}
                  trend="Ruta Cajero"
                />
              </div>
            </>
          )}
        </section>

        <section
          className="space-y-4 md:space-y-6 animate-fade-in"
          style={{ animationDelay: "0.5s" }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                Cursos en Progreso
              </h2>
              <p className="text-sm md:text-base text-muted-foreground mt-1">
                Continúa donde lo dejaste
              </p>
            </div>
            <Link to="/my-learning">
              <Button
                variant="outline"
                size="default"
                className="w-full sm:w-auto min-h-[44px] font-semibold"
              >
                Ver Todos
              </Button>
            </Link>
          </div>

          {isLoadingInProgress ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {Array.from({ length: 3 }).map((_, index) => (
                <CourseCardSkeleton key={index} />
              ))}
            </div>
          ) : (
            inProgressCourses.length > 0 && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {inProgressCourses.map((course) => (
                    <CourseCard
                      key={course.id}
                      course={course}
                      imageUrl={
                        imageUrls && imageUrls[course.id]
                          ? imageUrls[course.id]
                          : undefined
                      }
                    />
                  ))}
                </div>
                <div className="pt-2">
                  <PaginationControls
                    currentPage={inProgressPagination.pagination.pageIndex}
                    totalPages={Math.ceil(inProgressTotal / inProgressPagination.pagination.pageSize)}
                    onPageChange={inProgressPagination.setPage}
                    onNext={() =>
                      inProgressPagination.nextPage(
                        Math.ceil(inProgressTotal / inProgressPagination.pagination.pageSize)
                      )
                    }
                    onPrevious={inProgressPagination.prevPage}
                  />
                </div>
              </>
            )
          )}
        </section>
      </div>
    </Layout>
  );
};

export default Dashboard;
